import React, { useEffect, useState } from "react";
import { Grid, Paper, Typography, Box, CircularProgress, Tooltip } from "@mui/material";
import {
  People as PeopleIcon,
  Chat as ChatIcon,
  AttachMoney as MoneyIcon,
  Bolt as BoltIcon,
  TrendingUp as TrendingIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import StatCard from "./StatCard";
import { formatNumber, formatCurrency } from "./validation";
import { AI_REPORT_OVERVIEW } from "../../../config/urls";
import { useAuth } from "../../../hooks/AuthProvider";

const OverviewTab = ({ startDate, endDate }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState({
    active_users: 0,
    total_conversations: 0,
    total_requests: 0,
    total_tokens: 0,
    total_cost: 0,
    average_response_time: null,
    success_rate: 0,
  });

  const [trendData, setTrendData] = useState([]);
  const [currency, setCurrency] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getValidToken();

      const response = await fetch(
        `${AI_REPORT_OVERVIEW}?from_date=${startDate}&to_date=${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch overview data");

      const json = await response.json();
      if (!json.success) throw new Error("API returned failure");

      const data = json.data;

      setStats({
        active_users: data.active_users,
        total_conversations: data.total_conversations,
        total_requests: data.total_requests,
        total_tokens: data.total_tokens,
        total_cost: data.total_cost,
        average_response_time: data.average_response_time,
        success_rate: data.success_rate,
      });

      // Merge requests_per_day + users_per_day for chart
      const mergedTrend = data.requests_per_day.map((item) => {
        const usersObj = data.users_per_day.find((u) => u.date === item.date);

        return {
          date: item.date,
          requests: item.requests,
          users: usersObj ? usersObj.users : 0,
        };
      });

      setTrendData(mergedTrend);
      setCurrency(data.currency);
    } catch (err) {
      console.error(err);
      setError("Unable to load overview data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            icon={<PeopleIcon sx={{ color: "#ffffff" }} />}
            label="Active Users"
            value={formatNumber(stats.active_users)}
            trend="up"
            color="primary"
            description="Unique users who interacted with the AI at least once in the selected date range."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            icon={<ChatIcon sx={{ color: "#ffffff" }} />}
            label="Total Conversations"
            value={formatNumber(stats.total_conversations)}
            trend="up"
            color="success"
            description="Total AI conversations initiated by users during the selected period."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            icon={<MoneyIcon sx={{ color: "#ffffff" }} />}
            label="Total Cost"
            value={formatCurrency(stats.total_cost, currency)}
            trend="up"
            color="error"
            valueColor="error.main"
            description="Sum of the recorded cost of every AI call in this period, at each model's list price that day. See Token Usage for the breakdown."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            icon={<TrendingIcon sx={{ color: "#ffffff" }} />}
            label="Total Requests"
            value={formatNumber(stats.total_requests)}
            trend="up"
            color="purple"
            description="Total number of API requests made to the AI during the selected period."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            icon={<BoltIcon sx={{ color: "#ffffff" }} />}
            label="Avg Response Time"
            value={
              stats.average_response_time
                ? `${stats.average_response_time}s`
                : "N/A"
            }
            trend="neutral"
            color="warning"
            description="Average time taken by the AI to generate a response."
          />
        </Grid>
      </Grid>

   <Paper 
  elevation={0} 
  sx={{ 
    p: 3, 
    mb: 4, 
    boxShadow: "0px 1px 3px 0px #0000001A",
    borderRadius: "8px"
  }}
>
  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
    Daily Activity Trend
  </Typography>
  <Box sx={{ height: 350, mt: 2 }}>
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={trendData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" />
        <YAxis yAxisId="left" />
        <YAxis yAxisId="right" orientation="right" />
        <RechartsTooltip />
        <Legend />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="requests"
          stroke="#1e3c72"
          strokeWidth={2}
          name="Requests"
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="users"
          stroke="#16a34a"
          strokeWidth={2}
          name="Users"
        />
      </LineChart>
    </ResponsiveContainer>
  </Box>
</Paper>

<Grid container spacing={3}>
  <Grid item xs={12} sm={6}>
    <Tooltip title="Combined number of input and output tokens processed by the AI.">
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          boxShadow: "0px 1px 3px 0px #0000001A",
          borderRadius: "8px",
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0px 4px 8px 0px #0000001A"
          }
        }}
      >
        <Typography variant="caption" color="text.secondary">
          TOTAL TOKENS
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, my: 1 }}>
          {(stats.total_tokens / 1_000_000).toFixed(2)}M
        </Typography>
      </Paper>
    </Tooltip>
  </Grid>
  <Grid item xs={12} sm={6}>
    <Tooltip title="Percentage of AI responses that completed successfully without errors.">
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          boxShadow: "0px 1px 3px 0px #0000001A",
          borderRadius: "8px",
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0px 4px 8px 0px #0000001A"
          }
        }}
      >
        <Typography variant="caption" color="text.secondary">
          SUCCESS RATE
        </Typography>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, my: 1, color: "success.main" }}
        >
          {stats.success_rate}%
        </Typography>
      </Paper>
    </Tooltip>
  </Grid>
</Grid>
    </Box>
  );
};

export default OverviewTab;
